import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminLogout } from '../../api/admin';
import AdminSecureStorage from '../../utils/adminSecureStorage';
import { axiosInstance } from '../../utils/axios';
import ImageUpload from '../../components/ImageUpload';

interface Game {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  imageUrl?: string;
  status: 'ACTIVE' | 'INACTIVE' | 'MAINTENANCE';
  maxPlayPerDay: number;
  resetTime: string;
  gamePath: string;
  gameConfig: any;
  isCompetitive: boolean;
  prizeDescription?: string;
  livesPrice: number;
  livesPerUnit: number;
  livesCurrency: string;
  allowLivesPurchase: boolean;
  createdAt: string;
  updatedAt: string;
}

const AdminGames = () => {
  const navigate = useNavigate();
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingGame, setEditingGame] = useState<Game | null>(null);

  useEffect(() => {
    // Check authentication
    if (!AdminSecureStorage.isAdminTokenValid()) {
      navigate('/admin/login');
      return;
    }

    // Load games
    loadGames();
  }, [navigate]);

  const loadGames = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/admin/api/games');
      console.log('Games response:', response.data);
      setGames(response.data.data || []);
    } catch (error: any) {
      console.error('Error loading games:', error);
      setError(error.response?.data?.message || error.message || 'Failed to load games');
      
      // If unauthorized, redirect to login
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await adminLogout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      AdminSecureStorage.clearAll();
      navigate('/admin/login');
    }
  };

  const handleCreateGame = () => {
    setShowCreateForm(true);
    setEditingGame(null);
  };

  const handleEditGame = (game: Game) => {
    setEditingGame(game);
    setShowCreateForm(true);
  };

  const handleDeleteGame = async (gameId: string) => {
    if (!confirm('Are you sure you want to deactivate this game?')) {
      return;
    }

    try {
      await axiosInstance.delete(`/admin/api/games/${gameId}`);
      // Reload games
      await loadGames();
    } catch (error: any) {
      console.error('Error deactivating game:', error);
      setError(error.response?.data?.message || error.message || 'Failed to deactivate game');
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      ACTIVE: { color: 'bg-green-100 text-green-800', label: 'Active' },
      INACTIVE: { color: 'bg-red-100 text-red-800', label: 'Inactive' },
      MAINTENANCE: { color: 'bg-yellow-100 text-yellow-800', label: 'Maintenance' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.INACTIVE;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <span className="text-gray-600">Loading games...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg shadow-md max-w-md w-full">
          <div className="text-center">
            <div className="text-red-500 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Games</h3>
            <p className="text-gray-500 mb-4">{error}</p>
            <button
              onClick={loadGames}
              className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Manage Games</h1>
              <p className="text-sm text-gray-500">Create and manage games for the customer portal</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
              >
                Back to Dashboard
              </button>
              <button
                onClick={handleLogout}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Action Bar */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-lg font-medium text-gray-900">All Games</h2>
            <p className="text-sm text-gray-500">{games.length} games total</p>
          </div>
          <button
            onClick={handleCreateGame}
            className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span>Create New Game</span>
          </button>
        </div>

        {/* Games List */}
        {games.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1.5a2.5 2.5 0 005 0H17m-8 3.5V14m4-3.5V14M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No games yet</h3>
            <p className="text-gray-500 mb-6">Create your first game to get started</p>
            <button
              onClick={handleCreateGame}
              className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Create Your First Game
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {games.map((game) => (
              <div key={game.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{game.displayName}</h3>
                      <p className="text-sm text-gray-500 mb-2">{game.name}</p>
                      {game.description && (
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{game.description}</p>
                      )}
                    </div>
                    {getStatusBadge(game.status)}
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Max plays/day:</span>
                      <span className="font-medium">{game.maxPlayPerDay}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Reset time:</span>
                      <span className="font-medium">{game.resetTime}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Competitive:</span>
                      <span className="font-medium">{game.isCompetitive ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Lives purchase:</span>
                      <span className="font-medium">
                        {game.allowLivesPurchase ? (
                          <span className="text-green-600">
                            {(game as any).livesCurrency || 'AUD'} {Number(game.livesPrice).toFixed(2)} = {(game as any).livesPerUnit || (game as any).livesPerDollar} lives
                          </span>
                        ) : (
                          <span className="text-gray-500">Disabled</span>
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditGame(game)}
                      className="flex-1 bg-indigo-600 text-white px-3 py-2 rounded-md text-sm hover:bg-indigo-700 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteGame(game.id)}
                      className="flex-1 bg-red-600 text-white px-3 py-2 rounded-md text-sm hover:bg-red-700 transition-colors"
                    >
                      Deactivate
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Game Form Modal */}
        {showCreateForm && (
          <GameFormModal
            game={editingGame}
            onClose={() => {
              setShowCreateForm(false);
              setEditingGame(null);
            }}
            onSuccess={() => {
              setShowCreateForm(false);
              setEditingGame(null);
              loadGames();
            }}
          />
        )}
      </div>
    </div>
  );
};

// Game Form Modal Component
interface GameFormModalProps {
  game: Game | null;
  onClose: () => void;
  onSuccess: () => void;
}

const GameFormModal: React.FC<GameFormModalProps> = ({ game, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: game?.name || '',
    displayName: game?.displayName || '',
    description: game?.description || '',
    imageUrl: game?.imageUrl || '',
    maxPlayPerDay: game?.maxPlayPerDay || 3,
    resetTime: game?.resetTime ? game.resetTime.substring(0, 5) : '00:00', // Convert HH:MM:SS to HH:MM
    isCompetitive: game?.isCompetitive || false,
    prizeDescription: game?.prizeDescription || '',
    status: game?.status || 'ACTIVE',
    livesPrice: game?.livesPrice || 1.00,
  livesPerUnit: (game as any)?.livesPerUnit || (game as any)?.livesPerDollar || 10,
  livesCurrency: (game as any)?.livesCurrency || 'AUD',
    allowLivesPurchase: game?.allowLivesPurchase ?? true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    console.log('Submitting game with formData:', formData);

    try {
      const url = game ? `/admin/api/games/${game.id}` : '/admin/api/games';
      const method = game ? 'PUT' : 'POST';
      
      const response = await axiosInstance({
        method,
        url,
        data: formData
      });

      console.log('Game save response:', response.data);
      onSuccess();
    } catch (error: any) {
      console.error('Error saving game:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error message:', error.response?.data?.error || error.response?.data?.message);
      setError(error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to save game');
    } finally {
      setLoading(false);
    }
  };

  const validateField = (name: string, value: any) => {
    const errors = { ...validationErrors };
    
    if (name === 'name') {
      if (!value) {
        errors.name = 'Game name is required';
      } else if (!/^[a-zA-Z0-9]+$/.test(value)) {
        errors.name = 'Game name must only contain alphanumeric characters';
      } else if (value.length < 3) {
        errors.name = 'Game name must be at least 3 characters long';
      } else if (value.length > 50) {
        errors.name = 'Game name must be at most 50 characters long';
      } else {
        delete errors.name;
      }
    }
    
    if (name === 'displayName') {
      if (!value) {
        errors.displayName = 'Display name is required';
      } else if (value.length < 3) {
        errors.displayName = 'Display name must be at least 3 characters long';
      } else if (value.length > 200) {
        errors.displayName = 'Display name must be at most 200 characters long';
      } else {
        delete errors.displayName;
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
    
    validateField(name, newValue);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              {game ? 'Edit Game' : 'Create New Game'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                  Game Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    validationErrors.name 
                      ? 'border-red-300 focus:ring-red-500' 
                      : 'border-gray-300 focus:ring-indigo-500'
                  }`}
                  placeholder="e.g., vivatapsgoldrush"
                />
                {validationErrors.name && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Only alphanumeric characters allowed (a-z, A-Z, 0-9)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                  Display Name *
                </label>
                <input
                  type="text"
                  name="displayName"
                  value={formData.displayName}
                  onChange={handleChange}
                  required
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    validationErrors.displayName 
                      ? 'border-red-300 focus:ring-red-500' 
                      : 'border-gray-300 focus:ring-indigo-500'
                  }`}
                  placeholder="e.g., Viva Taps Gold Rush"
                />
                {validationErrors.displayName && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.displayName}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Describe the game..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                Game Image (Optional)
              </label>
              <ImageUpload
                value={formData.imageUrl}
                onChange={(url) => setFormData(prev => ({ ...prev, imageUrl: url }))}
                folder="games"
                optional={true}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                  Max Plays Per Day
                </label>
                <input
                  type="number"
                  name="maxPlayPerDay"
                  value={formData.maxPlayPerDay}
                  onChange={handleChange}
                  min="1"
                  max="100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                  Reset Time
                </label>
                <input
                  type="time"
                  name="resetTime"
                  value={formData.resetTime}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                Prize Description
              </label>
              <input
                type="text"
                name="prizeDescription"
                value={formData.prizeDescription}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="e.g., Winner gets $100 gift card"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                  <option value="MAINTENANCE">Maintenance</option>
                </select>
              </div>

              <div className="flex items-start">
                <input
                  type="checkbox"
                  name="isCompetitive"
                  checked={formData.isCompetitive}
                  onChange={handleChange}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded mt-1"
                />
                <div className="ml-2">
                  <label className="block text-sm text-gray-700 text-left">
                    Competitive Game
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Enable leaderboards and monthly competitions for this game
                  </p>
                </div>
              </div>
            </div>

            {/* Lives Purchase Settings */}
            <div className="border-t pt-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Lives Purchase Settings</h3>
              
              <div className="flex items-start mb-4">
                <input
                  type="checkbox"
                  name="allowLivesPurchase"
                  checked={formData.allowLivesPurchase}
                  onChange={handleChange}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded mt-1"
                />
                <div className="ml-2">
                  <label className="block text-sm text-gray-700 text-left">
                    Allow Lives Purchase
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Enable customers to buy extra lives for this game
                  </p>
                </div>
              </div>

              {formData.allowLivesPurchase && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                      Price Per Unit
                    </label>
                    <input
                      type="number"
                      name="livesPrice"
                      value={formData.livesPrice}
                      onChange={handleChange}
                      min="1"
                      max="999.99"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="1.00"
                    />
                    <p className="text-xs text-gray-500 mt-1">Minimum price per unit is 1 {formData.livesCurrency || 'AUD'}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                      Lives Per Unit
                    </label>
                    <input
                      type="number"
                      name="livesPerUnit"
                      value={formData.livesPerUnit}
                      onChange={handleChange}
                      min="1"
                      max="1000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="10"
                    />
                    <p className="text-xs text-gray-500 mt-1">How many extra lives are granted for each purchase (per unit of price)</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                      Currency
                    </label>
                    <select
                      name="livesCurrency"
                      value={formData.livesCurrency}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      {['AUD','USD','EUR','GBP','AED','CAD','NZD','JPY','INR'].map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Currency for pricing</p>
                  </div>
                </div>
              )}

              {formData.allowLivesPurchase && (
                <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <strong>Preview:</strong> {formData.livesCurrency} {formData.livesPrice} = {formData.livesPerUnit} extra lives 
                    ({(formData.livesPrice && formData.livesPerUnit ? (Number(formData.livesPrice) / Number(formData.livesPerUnit)) : 0).toFixed(4)} {formData.livesCurrency} per life)
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Saving...' : (game ? 'Update Game' : 'Create Game')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminGames;
